import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import { getValidToken } from '../../utils/tokenRefresh';
import './StudentDetailView.css';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gradeLevel?: string;
  tier: string;
  parentId: string;
  parentEmail?: string;
  email?: string;
  phone?: string;
  address?: string;
  profileNotes?: string;
  learningGoals?: string;
  status?: string;
  enrollmentDate?: string;
}

interface CompetitionEnrollment {
  id: string;
  competition_id: string;
  competition_name?: string;
  team_name?: string;
  team_size?: number;
  enrollment_date: string;
  session_pack_cost?: number;
  tshirt_cost?: number;
  team_registration_share?: number;
  tournament_registration_share?: number;
  other_costs_share?: number;
  total_cost: number;
  amount_paid: number;
  payment_status?: string;
  status: string;
}

interface Competition {
  id: string;
  name: string;
  competition_type?: string;
  tier: string;
  season_start: string;
  season_end: string;
  package_type_id?: string;
  package_name?: string;
  package_sessions?: number;
  package_price?: number;
  tshirt_cost?: number;
  team_registration_fee?: number;
  tournament_registration_fee?: number;
  other_costs?: number;
  other_costs_description?: string;
  status: string;
}

interface AttendanceRecord {
  id: string;
  date: string;
  status: 'present' | 'absent' | 'late' | 'excused' | 'scheduled';
  notes?: string;
  sessionName?: string;
  programId?: string;
  attendanceTime?: string;
  isScheduledOnly?: boolean;
}

interface SessionSchedule {
  id?: string;
  date: string;
  startTime: string;
  endTime: string;
  startTimeDisplay?: string;
  endTimeDisplay?: string;
  sessionType?: string;
  programName?: string;
  programId?: string;
  notes?: string;
}

interface GuardianContact {
  id: string;
  kind: 'guardian_user' | 'linked_contact';
  owner_user_id?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  alternate_phone?: string;
  user_type?: string;
  relationship_to_student?: string;
  is_primary: boolean;
  source?: string;
}

interface StudentDetailViewProps {
  student: Student;
  onClose: () => void;
  onUpdate: () => void;
}

const StudentDetailView: React.FC<StudentDetailViewProps> = ({
  student: initialStudent,
  onClose,
  onUpdate
}) => {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [activeSection, setActiveSection] = useState<'profile' | 'guardians' | 'schedule' | 'attendance' | 'competitions'>('profile');
  const [student, setStudent] = useState<Student>(initialStudent);
  const [isEditing, setIsEditing] = useState(false);
  const [editedStudent, setEditedStudent] = useState<Student>(initialStudent);
  const [loading, setLoading] = useState(false);
  
  // Competition states
  const [competitionEnrollments, setCompetitionEnrollments] = useState<CompetitionEnrollment[]>([]);
  const [availableCompetitions, setAvailableCompetitions] = useState<Competition[]>([]);
  const [showAddCompetition, setShowAddCompetition] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<string>('');
  const [teamName, setTeamName] = useState('');
  const [teamSize, setTeamSize] = useState<number>(5);

  // Schedule states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [sessionStartTime, setSessionStartTime] = useState('15:00');
  const [sessionEndTime, setSessionEndTime] = useState('16:00');
  const [schedules, setSchedules] = useState<SessionSchedule[]>([]);
  const [enrolledPrograms, setEnrolledPrograms] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const [existingSessionDates, setExistingSessionDates] = useState<Set<string>>(new Set());

  // Attendance states
  const [attendanceMonth, setAttendanceMonth] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [attendanceSchedules, setAttendanceSchedules] = useState<SessionSchedule[]>([]);
  const [guardians, setGuardians] = useState<GuardianContact[]>([]);
  const [guardiansLoading, setGuardiansLoading] = useState(false);

  // Program enrollment states (for profile tab)
  const [showEnrollProgram, setShowEnrollProgram] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState<any[]>([]);
  const [selectedProgramForEnrollment, setSelectedProgramForEnrollment] = useState<string>('');
  const [enrollmentStartDate, setEnrollmentStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [enrollmentEndDate, setEnrollmentEndDate] = useState<string>('');
  const [showEditEnrollment, setShowEditEnrollment] = useState(false);
  const [selectedEnrollmentForEdit, setSelectedEnrollmentForEdit] = useState<any>(null);
  const [editEnrollmentStartDate, setEditEnrollmentStartDate] = useState<string>('');
  const [editEnrollmentEndDate, setEditEnrollmentEndDate] = useState<string>('');
  const [editEnrollmentStatus, setEditEnrollmentStatus] = useState<string>('active');
  const [editEnrollmentNotes, setEditEnrollmentNotes] = useState<string>('');

  // Session pack states
  const [sessionPacks, setSessionPacks] = useState<any[]>([]);
  const [showAddSessionPack, setShowAddSessionPack] = useState(false);
  const [showEditSessionPack, setShowEditSessionPack] = useState(false);
  const [selectedSessionPack, setSelectedSessionPack] = useState<any>(null);
  const [availablePackageTypes, setAvailablePackageTypes] = useState<any[]>([]);
  const [sessionPackFormData, setSessionPackFormData] = useState({
    package_type_id: '',
    program_id: '',
    applicable_program_types: [] as string[],
    total_sessions: '',
    used_sessions: '0',
    purchase_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    original_price: '',
    discount_applied: '0',
    final_price: '',
    amount_paid: '',
    payment_status: 'paid',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    if (activeSection === 'profile') {
      fetchEnrolledPrograms();
      fetchAvailablePrograms();
      fetchSessionPacks();
      fetchAvailablePackageTypes();
    }
    if (activeSection === 'guardians') {
      fetchGuardians();
    }
    if (activeSection === 'competitions') {
      fetchCompetitionEnrollments();
      fetchAvailableCompetitions();
    } else if (activeSection === 'schedule') {
      fetchSchedules();
      fetchEnrolledPrograms();
    } else if (activeSection === 'attendance') {
      fetchAttendance();
      fetchAttendanceSchedules();
    }
  }, [activeSection, student.id, student.tier]);

  useEffect(() => {
    if (activeSection === 'schedule') {
      fetchSchedules();
    }
  }, [currentMonth]);

  useEffect(() => {
    if (activeSection === 'attendance') {
      fetchAttendance();
      fetchAttendanceSchedules();
    }
  }, [attendanceMonth]);

  // Fetch functions
  const fetchCompetitionEnrollments = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(
        `${config.BACKEND_URL}/api/competitions/student/${student.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCompetitionEnrollments(data);
      }
    } catch (error) {
      console.error('Error fetching competition enrollments:', error);
    }
  };

  const fetchAvailableCompetitions = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(
        `${config.BACKEND_URL}/api/competitions?tier=${student.tier}&status=active`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableCompetitions(data);
      }
    } catch (error) {
      console.error('Error fetching available competitions:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const response = await fetch(
        `${config.BACKEND_URL}/api/students/${student.id}/schedule?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        // Map the schedule data to our format
        const mappedSchedules = (data.schedule || []).map((item: any) => ({
          id: item.session_id,
          date: item.schedule_date ? String(item.schedule_date).split('T')[0] : '',
          startTime: item.start_time,
          endTime: item.end_time,
          startTimeDisplay: formatTimeDisplay(item.start_time),
          endTimeDisplay: formatTimeDisplay(item.end_time),
          sessionType: 'regular',
          programName: item.program_name,
          programId: item.program_id,
          notes: item.session_description || ''
        }));
        setSchedules(mappedSchedules);
        
        // Track existing session dates for color mapping
        const sessionDates: Set<string> = new Set(mappedSchedules.map((s: SessionSchedule) => s.date));
        setExistingSessionDates(sessionDates);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
      setExistingSessionDates(new Set());
    }
  };

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const startDate = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth(), 1);
      const endDate = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth() + 1, 0);
      
      const response = await fetch(
        `${config.BACKEND_URL}/api/attendance/records?student_id=${student.id}&start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('📅 Fetched attendance records:', data.records?.length || 0, 'records');
        // Map the attendance data to our format
        // This endpoint now includes scheduled sessions with status='scheduled'
        const mappedAttendance = (data.records || []).map((record: any) => ({
          id: record.id,
          date: record.attendance_date ? String(record.attendance_date).split('T')[0] : '',
          status: record.status as 'present' | 'absent' | 'late' | 'excused' | 'scheduled',
          notes: record.notes,
          sessionName: record.program_name,
          programId: record.program_id,
          attendanceTime: record.attendance_time,
          isScheduledOnly: record.is_scheduled_only || false
        }));
        setAttendanceRecords(mappedAttendance);
        console.log('📅 Mapped attendance records:', mappedAttendance.length);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendanceRecords([]);
    }
  };

  // Helper function to format time for display (times are already in PST from backend)
  const formatTimeDisplay = (timeStr: string): string => {
    if (!timeStr) return '';
    try {
      // Times from backend are already in PST, just format them nicely
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeStr;
    }
  };

  const fetchAttendanceSchedules = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const startDate = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth(), 1);
      const endDate = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth() + 1, 0);
      
      const response = await fetch(
        `${config.BACKEND_URL}/api/students/${student.id}/schedule?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const mappedSchedules = (data.schedule || []).map((item: any) => ({
          id: item.session_id,
          date: item.schedule_date ? String(item.schedule_date).split('T')[0] : '',
          startTime: item.start_time,
          endTime: item.end_time,
          startTimeDisplay: formatTimeDisplay(item.start_time),
          endTimeDisplay: formatTimeDisplay(item.end_time),
          programName: item.program_name,
          programId: item.program_id
        }));
        setAttendanceSchedules(mappedSchedules);
      }
    } catch (error) {
      console.error('Error fetching attendance schedules:', error);
      setAttendanceSchedules([]);
    }
  };

  const fetchGuardians = async () => {
    try {
      setGuardiansLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(
        `${config.BACKEND_URL}/api/students/${student.id}/guardians`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setGuardians(data.guardians || []);
      } else {
        setGuardians([]);
      }
    } catch (error) {
      console.error('Error fetching guardians:', error);
      setGuardians([]);
    } finally {
      setGuardiansLoading(false);
    }
  };

  const fetchEnrolledPrograms = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      // Use the enrollments endpoint instead of student details
      const response = await fetch(
        `${config.BACKEND_URL}/api/enrollments/student/${student.id}?status=active`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('Enrolled programs fetched:', data);
        // The enrollments endpoint returns the data directly
        const programs = Array.isArray(data) ? data : (data.enrollments || []);
        setEnrolledPrograms(programs);
        if (programs.length > 0 && !selectedProgramId) {
          setSelectedProgramId(programs[0].program_id);
        }
      } else {
        console.error('Failed to fetch enrollments:', response.status);
        setEnrolledPrograms([]);
      }
    } catch (error) {
      console.error('Error fetching enrolled programs:', error);
      setEnrolledPrograms([]);
    }
  };

  const fetchAvailablePrograms = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      // Fetch programs matching student's tier
      const response = await fetch(
        `${config.BACKEND_URL}/api/programs/?status=active&tier=${student.tier}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('Available programs fetched for tier', student.tier, ':', data);
        const programs = data.programs || data || [];
        setAvailablePrograms(programs);
        console.log('Set available programs:', programs.length, 'programs for tier', student.tier);
      } else {
        console.error('Failed to fetch programs:', response.status, response.statusText);
        setAvailablePrograms([]);
      }
    } catch (error) {
      console.error('Error fetching available programs:', error);
      setAvailablePrograms([]);
    }
  };

  const fetchSessionPacks = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(
        `${config.BACKEND_URL}/api/packages/student/${student.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSessionPacks(data.packages || []);
      } else {
        console.error('Failed to fetch session packs:', response.status);
        setSessionPacks([]);
      }
    } catch (error) {
      console.error('Error fetching session packs:', error);
      setSessionPacks([]);
    }
  };

  const fetchAvailablePackageTypes = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(
        `${config.BACKEND_URL}/api/package-types?tier=${student.tier}&status=active`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailablePackageTypes(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch package types:', response.status);
        setAvailablePackageTypes([]);
      }
    } catch (error) {
      console.error('Error fetching package types:', error);
      setAvailablePackageTypes([]);
    }
  };

  // Profile handlers
  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      const response = await fetch(`${config.BACKEND_URL}/api/students/${student.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: editedStudent.firstName,
          last_name: editedStudent.lastName,
          date_of_birth: editedStudent.dateOfBirth,
          grade_level: editedStudent.gradeLevel,
          tier: editedStudent.tier,
          email: editedStudent.email,
          phone: editedStudent.phone,
          address: editedStudent.address,
          profile_notes: editedStudent.profileNotes,
          learning_goals: editedStudent.learningGoals,
          status: editedStudent.status
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStudent(data);
        setIsEditing(false);
        // Update editedStudent to match the updated student
        setEditedStudent(data);
        // Refresh available programs since tier might have changed
        await fetchAvailablePrograms();
        // Refresh enrolled programs
        await fetchEnrolledPrograms();
        onUpdate();
        alert('✅ Student profile updated successfully!');
      } else {
        const error = await response.json();
        alert(`❌ Failed to update: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating student:', error);
      alert('❌ An error occurred while updating');
    } finally {
      setLoading(false);
    }
  };

  // Session pack handlers
  const handleAddSessionPack = async () => {
    if (!sessionPackFormData.total_sessions || !sessionPackFormData.purchase_date) {
      alert('Please fill in required fields (total sessions, purchase date)');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      // Calculate expiry date if not provided (9 months from purchase date)
      let expiryDate = sessionPackFormData.expiry_date;
      if (!expiryDate && sessionPackFormData.purchase_date) {
        const purchaseDate = new Date(sessionPackFormData.purchase_date);
        const expiry = new Date(purchaseDate);
        expiry.setMonth(expiry.getMonth() + 9);
        expiryDate = expiry.toISOString().split('T')[0];
      }

      const packageData = {
        student_id: student.id,
        package_type_id: sessionPackFormData.package_type_id || null,
        program_id: sessionPackFormData.program_id || null,
        applicable_program_types: sessionPackFormData.applicable_program_types,
        total_sessions: parseInt(sessionPackFormData.total_sessions),
        used_sessions: parseInt(sessionPackFormData.used_sessions || '0'),
        purchase_date: sessionPackFormData.purchase_date,
        expiry_date: expiryDate,
        original_price: parseFloat(sessionPackFormData.original_price || '0'),
        discount_applied: parseFloat(sessionPackFormData.discount_applied || '0'),
        final_price: parseFloat(sessionPackFormData.final_price || sessionPackFormData.original_price || '0'),
        amount_paid: parseFloat(sessionPackFormData.amount_paid || sessionPackFormData.final_price || '0'),
        payment_status: sessionPackFormData.payment_status,
        status: sessionPackFormData.status,
        notes: sessionPackFormData.notes || null
      };

      const response = await fetch(`${config.BACKEND_URL}/api/packages/admin/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(packageData)
      });

      if (response.ok) {
        alert('✅ Session pack added successfully!');
        setShowAddSessionPack(false);
        resetSessionPackForm();
        fetchSessionPacks();
        onUpdate();
      } else {
        const error = await response.json();
        alert(`❌ Failed to add session pack: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding session pack:', error);
      alert('❌ An error occurred while adding session pack');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSessionPack = async () => {
    if (!selectedSessionPack) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      const packageData: any = {};
      if (sessionPackFormData.total_sessions) packageData.total_sessions = parseInt(sessionPackFormData.total_sessions);
      if (sessionPackFormData.used_sessions !== undefined) packageData.used_sessions = parseInt(sessionPackFormData.used_sessions);
      if (sessionPackFormData.purchase_date) packageData.purchase_date = sessionPackFormData.purchase_date;
      if (sessionPackFormData.expiry_date) packageData.expiry_date = sessionPackFormData.expiry_date;
      if (sessionPackFormData.original_price) packageData.original_price = parseFloat(sessionPackFormData.original_price);
      if (sessionPackFormData.discount_applied !== undefined) packageData.discount_applied = parseFloat(sessionPackFormData.discount_applied);
      if (sessionPackFormData.final_price) packageData.final_price = parseFloat(sessionPackFormData.final_price);
      if (sessionPackFormData.amount_paid) packageData.amount_paid = parseFloat(sessionPackFormData.amount_paid);
      if (sessionPackFormData.payment_status) packageData.payment_status = sessionPackFormData.payment_status;
      if (sessionPackFormData.status) packageData.status = sessionPackFormData.status;
      if (sessionPackFormData.notes !== undefined) packageData.notes = sessionPackFormData.notes;
      if (sessionPackFormData.program_id !== undefined) packageData.program_id = sessionPackFormData.program_id || null;
      if (sessionPackFormData.applicable_program_types !== undefined) {
        packageData.applicable_program_types = sessionPackFormData.applicable_program_types;
      }

      const response = await fetch(`${config.BACKEND_URL}/api/packages/admin/${selectedSessionPack.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(packageData)
      });

      if (response.ok) {
        alert('✅ Session pack updated successfully!');
        setShowEditSessionPack(false);
        setSelectedSessionPack(null);
        resetSessionPackForm();
        fetchSessionPacks();
        onUpdate();
      } else {
        const error = await response.json();
        alert(`❌ Failed to update session pack: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating session pack:', error);
      alert('❌ An error occurred while updating session pack');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSessionPack = async (packageId: string) => {
    if (!window.confirm('Are you sure you want to delete this session pack? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      // Get a valid token (will refresh if expired)
      const token = await getValidToken();
      
      if (!token) {
        alert('❌ Authentication required. Please log in again.');
        return;
      }
      
      const response = await fetch(`${config.BACKEND_URL}/api/packages/admin/${packageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Session pack deleted successfully!');
        fetchSessionPacks();
        onUpdate();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`❌ Failed to delete session pack: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting session pack:', error);
      alert('❌ An error occurred while deleting session pack');
    } finally {
      setLoading(false);
    }
  };

  const resetSessionPackForm = () => {
    setSessionPackFormData({
      package_type_id: '',
      program_id: '',
      applicable_program_types: [],
      total_sessions: '',
      used_sessions: '0',
      purchase_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      original_price: '',
      discount_applied: '0',
      final_price: '',
      amount_paid: '',
      payment_status: 'paid',
      status: 'active',
      notes: ''
    });
  };

  const handleEditSessionPackClick = (pack: any) => {
    setSelectedSessionPack(pack);
    setSessionPackFormData({
      package_type_id: pack.package_type_id || '',
      program_id: pack.program_id || '',
      applicable_program_types: Array.isArray(pack.applicable_program_types) ? pack.applicable_program_types : [],
      total_sessions: pack.total_sessions.toString(),
      used_sessions: pack.used_sessions.toString(),
      purchase_date: pack.purchase_date ? new Date(pack.purchase_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      expiry_date: pack.expiry_date ? new Date(pack.expiry_date).toISOString().split('T')[0] : '',
      original_price: pack.original_price ? parseFloat(pack.original_price).toString() : '',
      discount_applied: pack.discount_applied ? parseFloat(pack.discount_applied).toString() : '0',
      final_price: pack.final_price ? parseFloat(pack.final_price).toString() : '',
      amount_paid: pack.amount_paid ? parseFloat(pack.amount_paid).toString() : '',
      payment_status: pack.payment_status || 'paid',
      status: pack.status || 'active',
      notes: pack.notes || ''
    });
    setShowEditSessionPack(true);
  };

  const toggleApplicableProgramType = (programType: string) => {
    setSessionPackFormData((prev) => {
      const current = Array.isArray(prev.applicable_program_types) ? prev.applicable_program_types : [];
      const exists = current.includes(programType);
      const next = exists
        ? current.filter((item) => item !== programType)
        : [...current, programType];
      return { ...prev, applicable_program_types: next };
    });
  };

  const handleEnrollInProgram = async () => {
    if (!selectedProgramForEnrollment) {
      alert('Please select a program');
      return;
    }

    try {
      setLoading(true);
      const token = await getValidToken();
      
      if (!token) {
        alert('❌ Authentication required. Please log in again.');
        return;
      }
      
      // Create enrollment
      const response = await fetch(`${config.BACKEND_URL}/api/enrollments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_id: student.id,
          program_id: selectedProgramForEnrollment,
          enrollment_date: enrollmentStartDate,
          start_date: enrollmentStartDate,
          end_date: enrollmentEndDate || null,
          status: 'active'
        })
      });

      if (response.ok) {
        alert('✅ Student enrolled in program successfully!');
        setShowEnrollProgram(false);
        setSelectedProgramForEnrollment('');
        setEnrollmentStartDate(new Date().toISOString().split('T')[0]);
        setEnrollmentEndDate('');
        fetchEnrolledPrograms();
        onUpdate();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`❌ Failed to enroll: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error enrolling in program:', error);
      alert('❌ An error occurred while enrolling');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrollFromProgram = async (enrollmentId: string, programName: string) => {
    if (!window.confirm(`Are you sure you want to unenroll this student from "${programName}"? This will mark the enrollment as dropped.`)) {
      return;
    }

    try {
      setLoading(true);
      // Get a valid token (will refresh if expired)
      const token = await getValidToken();
      
      if (!token) {
        alert('❌ Authentication required. Please log in again.');
        return;
      }
      
      const response = await fetch(`${config.BACKEND_URL}/api/enrollments/${enrollmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Student unenrolled from program successfully!');
        fetchEnrolledPrograms();
        onUpdate();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`❌ Failed to unenroll student: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error unenrolling student:', error);
      alert('❌ An error occurred while unenrolling student');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEnrollmentClick = (enrollment: any) => {
    setSelectedEnrollmentForEdit(enrollment);
    setEditEnrollmentStartDate(enrollment.start_date || enrollment.enrollment_date || new Date().toISOString().split('T')[0]);
    setEditEnrollmentEndDate(enrollment.end_date || '');
    setEditEnrollmentStatus(enrollment.status || 'active');
    setEditEnrollmentNotes(enrollment.notes || '');
    setShowEditEnrollment(true);
  };

  const handleUpdateEnrollment = async () => {
    if (!selectedEnrollmentForEdit) {
      return;
    }

    try {
      setLoading(true);
      const token = await getValidToken();
      
      if (!token) {
        alert('❌ Authentication required. Please log in again.');
        return;
      }
      
      const enrollmentId = selectedEnrollmentForEdit.enrollment_id || selectedEnrollmentForEdit.id;
      
      const response = await fetch(`${config.BACKEND_URL}/api/enrollments/${enrollmentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: editEnrollmentStartDate,
          end_date: editEnrollmentEndDate || null,
          enrollment_date: editEnrollmentStartDate, // For backward compatibility
          status: editEnrollmentStatus,
          notes: editEnrollmentNotes || null
        })
      });

      if (response.ok) {
        alert('✅ Enrollment updated successfully!');
        setShowEditEnrollment(false);
        setSelectedEnrollmentForEdit(null);
        fetchEnrolledPrograms();
        onUpdate();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`❌ Failed to update enrollment: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating enrollment:', error);
      alert('❌ An error occurred while updating enrollment');
    } finally {
      setLoading(false);
    }
  };

  // Schedule handlers
  // Schedule day click handler - cycle between no session and scheduled session
  const handleScheduleDayClick = async (dateOrStr: Date | string) => {
    const dateString = typeof dateOrStr === 'string' ? dateOrStr : dateOrStr.toISOString().split('T')[0];
    const existingSession = schedules.find(s => s.date === dateString);
    const displayDate = typeof dateOrStr === 'string' ? new Date(dateOrStr + 'T00:00:00').toLocaleDateString() : dateOrStr.toLocaleDateString();

    if (existingSession) {
      // Remove session - delete the course registration and session
      if (window.confirm(`Remove session on ${displayDate}?`)) {
        await handleRemoveSession(existingSession.id || '');
      }
    } else {
      // Create new session
      await handleCreateSession(dateString);
    }
  };

  const handleRemoveSession = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      
      // Delete course registration first
      const regResponse = await fetch(`${config.BACKEND_URL}/api/course-registrations?student_id=${student.id}&session_id=${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (regResponse.ok) {
        console.log('✅ Session removed');
        fetchSchedules();
      } else {
        const error = await regResponse.json();
        alert(`Failed to remove session: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error removing session:', error);
      alert('Failed to remove session');
    }
  };

  const handleCreateSession = async (dateStr: string) => {
    if (!selectedProgramId) {
      alert('Please select a program first');
      return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const normalizeTime = (timeValue?: string | null): string => {
        if (!timeValue) return '';
        const parts = String(timeValue).trim().split(':');
        if (parts.length < 2) return String(timeValue).trim();
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      };
      
      // Create new class session
      const sessionResponse = await fetch(`${config.BACKEND_URL}/api/class-sessions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          program_id: selectedProgramId,
          date_available: dateStr,  // Backend expects date_available, not session_date
          start_time: sessionStartTime,
          end_time: sessionEndTime,
          max_capacity: 20,
          status: 'scheduled'
        })
      });

      let sessionId = '';
      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json();
        sessionId = sessionData.id;
      } else if (sessionResponse.status === 409) {
        // Duplicate slot exists already; reuse the existing class session for enrollment.
        const existingResponse = await fetch(
          `${config.BACKEND_URL}/api/class-sessions/?program_id=${selectedProgramId}&from_date=${dateStr}&to_date=${dateStr}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!existingResponse.ok) {
          const conflictError = await sessionResponse.json().catch(() => ({ detail: 'Session already exists' }));
          alert(`Failed to create session: ${conflictError.detail || 'Session already exists'}`);
          return;
        }

        const existingSessions = await existingResponse.json().catch(() => []);
        const targetStart = normalizeTime(sessionStartTime);
        const targetEnd = normalizeTime(sessionEndTime);
        const matchingSession = (Array.isArray(existingSessions) ? existingSessions : []).find((s: any) => (
          s?.program_id === selectedProgramId &&
          normalizeTime(s?.start_time) === targetStart &&
          normalizeTime(s?.end_time) === targetEnd
        ));

        if (!matchingSession?.id) {
          const conflictError = await sessionResponse.json().catch(() => ({ detail: 'Session already exists' }));
          alert(`Failed to create session: ${conflictError.detail || 'Session already exists'}`);
          return;
        }

        sessionId = matchingSession.id;
      } else {
        const error = await sessionResponse.json();
        alert(`Failed to create session: ${error.detail || 'Unknown error'}`);
        return;
      }

      // Enroll student in the session
      const enrollResponse = await fetch(`${config.BACKEND_URL}/api/course-registrations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: student.id,
          programId: selectedProgramId,
          selectedSessions: [sessionId]  // Backend expects array
        })
      });

      if (enrollResponse.ok) {
        console.log('✅ Session created and student enrolled');
        fetchSchedules();
      } else if (enrollResponse.status === 409) {
        // Student may already be registered to this session; treat as successful scheduling.
        console.log('ℹ️ Student already enrolled in this session');
        fetchSchedules();
      } else {
        const error = await enrollResponse.json();
        alert(`Session created but enrollment failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Failed to create session');
    }
  };


  // Competition handlers
  const handleAddCompetition = async () => {
    if (!selectedCompetition) {
      alert('Please select a competition');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      
      const response = await fetch(`${config.BACKEND_URL}/api/competitions/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          competition_id: selectedCompetition,
          student_id: student.id,
          team_name: teamName || null,
          team_size: teamSize
        })
      });

      if (response.ok) {
        alert('✅ Student enrolled in competition successfully!');
        setShowAddCompetition(false);
        setSelectedCompetition('');
        setTeamName('');
        setTeamSize(5);
        fetchCompetitionEnrollments();
        onUpdate();
      } else {
        const error = await response.json();
        alert(`❌ Failed to enroll: ${error.detail || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error enrolling in competition:', error);
      alert('❌ An error occurred while enrolling');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCompetition = async (enrollmentId: string) => {
    if (!window.confirm('Are you sure you want to remove this competition enrollment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(
        `${config.BACKEND_URL}/api/competitions/enrollments/${enrollmentId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        alert('✅ Competition enrollment removed');
        fetchCompetitionEnrollments();
        onUpdate();
      } else {
        const error = await response.json();
        alert(`❌ Failed to remove: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error removing competition enrollment:', error);
      alert('❌ An error occurred');
    }
  };

  // Attendance handlers - cycle through states on click
  const handleAttendanceDayClick = async (event: React.MouseEvent, dateStr: string) => {
    event.preventDefault();
    
    // Get current attendance status
    const currentAttendance = attendanceRecords.find(r => r.date === dateStr);
    
    // Determine next status in cycle:
    // No attendance → Present → Absent → Late → Excused → No attendance (delete)
    let nextStatus: 'present' | 'absent' | 'late' | 'excused' | null = null;
    
    if (!currentAttendance) {
      nextStatus = 'present';
    } else {
      switch (currentAttendance.status) {
        case 'scheduled':
          // API returns scheduled-only rows (id ends with _scheduled); first click should mark present
          nextStatus = 'present';
          break;
        case 'present':
          nextStatus = 'absent';
          break;
        case 'absent':
          nextStatus = 'late';
          break;
        case 'late':
          nextStatus = 'excused';
          break;
        case 'excused':
          nextStatus = null; // Delete record
          break;
        default:
          console.warn('Unknown attendance status for day click:', currentAttendance.status);
          nextStatus = 'present';
          break;
      }
    }
    
    await handleMarkAttendance(dateStr, nextStatus);
  };

  const handleMarkAttendance = async (dateStr: string, status: 'present' | 'absent' | 'late' | 'excused' | null) => {
    
    // Find the scheduled session for this date - check both attendanceSchedules and attendanceRecords
    let scheduledSession = attendanceSchedules.find(s => s.date === dateStr);
    
    // If not found in schedules, check if there's a scheduled-only record in attendanceRecords
    if (!scheduledSession) {
      const scheduledRecord = attendanceRecords.find(r => r.date === dateStr && (r.status === 'scheduled' || r.isScheduledOnly));
      if (scheduledRecord) {
        // Create a scheduled session object from the record
        scheduledSession = {
          id: scheduledRecord.id?.replace('_scheduled', '') || '',
          date: scheduledRecord.date,
          startTime: scheduledRecord.attendanceTime || '15:00:00',
          endTime: scheduledRecord.attendanceTime || '16:00:00',
          programName: scheduledRecord.sessionName || 'Unknown Program',
          programId: scheduledRecord.programId || ''
        };
      }
    }
    
    if (!scheduledSession) {
      console.error('⚠️ No scheduled session found for date:', dateStr);
      console.log('Available schedules:', attendanceSchedules.map(s => ({ date: s.date, program: s.programName })));
      console.log('Available attendance records:', attendanceRecords.map(r => ({ date: r.date, status: r.status })));
      alert(`No scheduled session found for ${dateStr}. Please make sure the student has a session scheduled for this date.`);
      return;
    }

    try {
      const token = localStorage.getItem('cognitoToken') || '';
      
      // Check if attendance record already exists
      const existingRecord = attendanceRecords.find(r => r.date === dateStr);
      
      if (status === null) {
        // Delete the attendance record - only if it exists and has a real ID (not a scheduled-only ID)
        if (existingRecord && existingRecord.id && !existingRecord.id.includes('_scheduled')) {
          const response = await fetch(`${config.BACKEND_URL}/api/attendance/records/${existingRecord.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            console.log(`✅ Attendance cleared: ${dateStr}`);
            fetchAttendance();
            fetchAttendanceSchedules(); // Refresh schedules to show as scheduled again
          } else {
            const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
            console.error('❌ Failed to delete attendance:', error.detail);
            alert(`Failed to delete attendance: ${error.detail || 'Unknown error'}`);
          }
        } else {
          // No real attendance record to delete, just refresh
          console.log(`ℹ️ No attendance record to delete for ${dateStr}`);
          fetchAttendance();
          fetchAttendanceSchedules();
        }
      } else if (existingRecord && existingRecord.id && !existingRecord.id.includes('_scheduled')) {
        // Update existing record
        const response = await fetch(`${config.BACKEND_URL}/api/attendance/records/${existingRecord.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: status,
            notes: `Updated by admin`
          })
        });

        if (response.ok) {
          console.log(`✅ Attendance updated: ${dateStr} -> ${status}`);
          fetchAttendance();
          fetchAttendanceSchedules(); // Refresh schedules
        } else {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          console.error('❌ Failed to update attendance:', error.detail);
          alert(`Failed to update attendance: ${error.detail || 'Unknown error'}`);
        }
      } else {
        // Create new record
        // Format attendance_time from scheduled session if available
        let formattedTime = null;
        if (scheduledSession.startTime) {
          // Convert time format to HH:MM:SS
          const timeStr = scheduledSession.startTime;
          if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
            formattedTime = timeStr;
          } else {
            // Try to parse and format
            try {
              const timeParts = timeStr.split(':');
              if (timeParts.length >= 2) {
                formattedTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:00`;
              }
            } catch (e) {
              console.warn('Could not parse attendance_time:', timeStr);
            }
          }
        }
        
        const requestBody: any = {
          student_id: student.id,
          program_id: scheduledSession.programId,
          attendance_date: dateStr,
          status: status,
          notes: `Marked by admin`
        };
        
        // Only include attendance_time if it exists and is formatted
        if (formattedTime) {
          requestBody.attendance_time = formattedTime;
        }
        
        const response = await fetch(`${config.BACKEND_URL}/api/attendance/records`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (response.ok) {
          console.log(`✅ Attendance marked: ${dateStr} -> ${status}`);
          fetchAttendance();
          fetchAttendanceSchedules(); // Refresh schedules
        } else {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          console.error(`❌ Failed to mark attendance: ${error.detail || 'Unknown error'}`);
          alert(`Failed to mark attendance: ${error.detail || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Could not update attendance. Check your connection and try again.');
    }
  };

  // Utility functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateAge = (dateOfBirth: string) => {
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getAttendanceStatus = (date: string): AttendanceRecord | undefined => {
    const record = attendanceRecords.find(r => r.date === date);
    // Scheduled-only rows from API are not a real mark; show as "scheduled" styling like no attendance
    if (record && (record.status === 'scheduled' || record.isScheduledOnly)) {
      return undefined;
    }
    return record;
  };

  const getScheduleForDate = (date: string): SessionSchedule | undefined => {
    return schedules.find(schedule => schedule.date === date);
  };

  const getAttendanceColor = (status: string): string => {
    switch (status) {
      case 'present': return '#10b981'; // green
      case 'absent': return '#ef4444'; // red
      case 'late': return '#f59e0b'; // orange
      case 'excused': return '#6366f1'; // indigo
      default: return '#e5e7eb'; // gray
    }
  };

  // Helper function to abbreviate program names for mobile
  const abbreviateProgramName = (name: string): string => {
    if (!isMobile) return name;
    
    // Common abbreviations
    const abbreviations: { [key: string]: string } = {
      'after school': 'AS',
      'afterschool': 'AS',
      'robotics': 'Rob',
      'coding': 'Code',
      'programming': 'Prog',
      'math': 'Math',
      'mathematics': 'Math',
      'science': 'Sci',
      'engineering': 'Eng',
      'foundations': 'Found',
      'innovators': 'Innov',
      'pioneers': 'Pion',
      'winter camp': 'WC',
      'summer camp': 'SC',
      'spring camp': 'SprC',
      'pro d': 'PD',
      'professional development': 'PD'
    };
    
    const nameLower = name.toLowerCase();
    
    // Check for exact matches first
    for (const [key, abbrev] of Object.entries(abbreviations)) {
      if (nameLower.includes(key)) {
        return abbrev;
      }
    }
    
    // If no match, use first letters of words (max 3 words, max 4 chars each)
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length === 1) {
      // Single word: take first 4 chars
      return words[0].substring(0, 4).toUpperCase();
    } else if (words.length === 2) {
      // Two words: first letter of each
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      // Three or more words: first letter of first 3 words
      return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
    }
  };

  // Calendar rendering
  const renderScheduleCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add days of the month (use YYYY-MM-DD from calendar to avoid timezone shifting)
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasExistingSession = existingSessionDates.has(dateString);
      const schedule = getScheduleForDate(dateString);
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${hasExistingSession ? 'has-existing-session' : ''}`}
          onClick={() => handleScheduleDayClick(dateString)}
          style={{ cursor: 'pointer' }}
          title={schedule ? `${schedule.programName}\n${schedule.startTimeDisplay || schedule.startTime} - ${schedule.endTimeDisplay || schedule.endTime}\nClick to remove` : 'Click to schedule session'}
        >
          <div className="day-number">{day}</div>
          {schedule && (
            <div className="schedule-indicator">
              <div className="schedule-time">{schedule.startTimeDisplay || schedule.startTime} - {schedule.endTimeDisplay || schedule.endTime}</div>
              <div className="schedule-program" title={schedule.programName}>
                {isMobile ? abbreviateProgramName(schedule.programName || '') : schedule.programName}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <button
            className="btn btn-sm"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          >
            ←
          </button>
          <h3>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
          <button
            className="btn btn-sm"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          >
            →
          </button>
        </div>
        
        <div className="calendar-grid">
          {dayNames.map(name => (
            <div key={name} className="calendar-day-name">{name}</div>
          ))}
          {days}
        </div>
      </div>
    );
  };

  const renderAttendanceCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(attendanceMonth);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Format date as YYYY-MM-DD without timezone conversion
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const attendance = getAttendanceStatus(dateString);
      // Check for scheduled session in both attendanceSchedules and attendanceRecords (scheduled-only records)
      let scheduledSession = attendanceSchedules.find(s => s.date === dateString);
      if (!scheduledSession) {
        const scheduledRecord = attendanceRecords.find(r => r.date === dateString && (r.status === 'scheduled' || r.isScheduledOnly));
        if (scheduledRecord) {
          scheduledSession = {
            id: scheduledRecord.id?.replace('_scheduled', '') || '',
            date: scheduledRecord.date,
            startTime: scheduledRecord.attendanceTime || '15:00:00',
            endTime: scheduledRecord.attendanceTime || '16:00:00',
            programName: scheduledRecord.sessionName || 'Unknown Program',
            programId: scheduledRecord.programId || ''
          };
        }
      }
      const isToday = new Date().toDateString() === date.toDateString();
      const hasSchedule = !!scheduledSession;

      // Determine background color
      let bgColor = undefined;
      let textColor = undefined;
      let borderColor = undefined;

      if (attendance) {
        // Has attendance marked - use attendance status color
        bgColor = getAttendanceColor(attendance.status);
        textColor = 'white';
      } else if (hasSchedule) {
        // Has scheduled session but no attendance - use light blue
        bgColor = '#e0f2fe';
        borderColor = '#0284c7';
        textColor = '#0369a1';
      }

      days.push(
        <div
          key={day}
          className={`calendar-day attendance clickable ${isToday ? 'today' : ''} ${hasSchedule ? 'has-schedule' : ''}`}
          style={{
            backgroundColor: bgColor,
            color: textColor,
            border: borderColor ? `2px solid ${borderColor}` : undefined,
            cursor: hasSchedule ? 'pointer' : 'default'
          }}
          onClick={(e) => hasSchedule ? handleAttendanceDayClick(e, dateString) : undefined}
          title={
            hasSchedule && scheduledSession
              ? `${scheduledSession.programName} (${scheduledSession.startTimeDisplay || scheduledSession.startTime} - ${scheduledSession.endTimeDisplay || scheduledSession.endTime})${attendance ? `\n${attendance.status.toUpperCase()}` : '\nClick to mark attendance'}` 
              : 'No scheduled session'
          }
        >
          <div className="day-number">{day}</div>
          {attendance && (
            <div className="attendance-indicator">
              <span className="attendance-icon">
                {attendance.status === 'present' && '✓'}
                {attendance.status === 'absent' && '✗'}
                {attendance.status === 'late' && '⏰'}
                {attendance.status === 'excused' && 'E'}
              </span>
            </div>
          )}
          {hasSchedule && !attendance && (
            <div className="schedule-indicator">
              <span style={{ fontSize: '10px' }}>📅</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <button
            className="btn btn-sm"
            onClick={() => {
              const newMonth = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth() - 1, 1);
              setAttendanceMonth(newMonth);
            }}
          >
            ←
          </button>
          <h3>{attendanceMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
          <button
            className="btn btn-sm"
            onClick={() => {
              const newMonth = new Date(attendanceMonth.getFullYear(), attendanceMonth.getMonth() + 1, 1);
              setAttendanceMonth(newMonth);
            }}
          >
            →
          </button>
        </div>
        
        <div className="attendance-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#10b981' }}></div>
            <span>✓ Present</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#ef4444' }}></div>
            <span>✗ Absent</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#f59e0b' }}></div>
            <span>⏰ Late</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#6366f1' }}></div>
            <span>E Excused</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#e0f2fe', border: '2px solid #0284c7' }}></div>
            <span>📅 Scheduled</span>
          </div>
        </div>
        
        <p className="instruction" style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
          💡 Click on any scheduled day to cycle: 📅 Scheduled → ✓ Present → ✗ Absent → ⏰ Late → E Excused → 📅 Scheduled
        </p>

        <div className="calendar-grid">
          {dayNames.map(name => (
            <div key={name} className="calendar-day-name">{name}</div>
          ))}
          {days}
        </div>

        <div className="attendance-summary">
          <h4>Monthly Summary</h4>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">Present:</span>
              <span className="stat-value">{attendanceRecords.filter(r => r.status === 'present').length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Absent:</span>
              <span className="stat-value">{attendanceRecords.filter(r => r.status === 'absent').length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Late:</span>
              <span className="stat-value">{attendanceRecords.filter(r => r.status === 'late').length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Excused:</span>
              <span className="stat-value">{attendanceRecords.filter(r => r.status === 'excused').length}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={isMobile ? { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0, 0, 0, 0.5)', 
      zIndex: 2000,
      overflow: 'hidden'
    } : {}}>
      <div 
        className={`modal-content student-detail-modal large ${isMobile ? 'mobile-view' : ''}`} 
        onClick={(e) => e.stopPropagation()}
        style={isMobile ? {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          maxWidth: '100%',
          maxHeight: '100vh',
          borderRadius: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        } : {}}
      >
        {/* Header */}
        <div className="student-detail-header" style={isMobile ? {
          padding: '15px 20px',
          flexShrink: 0
        } : {}}>
          <div>
            <h2 style={isMobile ? { fontSize: '18px', margin: 0 } : {}}>
              👤 {student.firstName} {student.lastName}
            </h2>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: isMobile ? '12px' : '14px' 
            }}>
              {student.tier.charAt(0).toUpperCase() + student.tier.slice(1)} Tier • Age {calculateAge(student.dateOfBirth)} • {student.status || 'Active'}
            </p>
          </div>
          <button 
            className="close-button" 
            onClick={onClose}
            style={isMobile ? {
              fontSize: '28px',
              padding: '8px 12px',
              minWidth: '44px',
              minHeight: '44px'
            } : {}}
          >
            ×
          </button>
        </div>

        {/* Navigation Tabs */}
        <div 
          className="student-detail-nav" 
          style={isMobile ? {
            display: 'flex',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            flexShrink: 0,
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          } : {}}
        >
          {isMobile && <style>{`
            .student-detail-nav::-webkit-scrollbar {
              display: none;
            }
          `}</style>}
          <button
            className={`detail-nav-tab ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
            style={isMobile ? {
              padding: '12px 16px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              flexShrink: 0
            } : {}}
          >
            📋 Profile
          </button>
          <button
            className={`detail-nav-tab ${activeSection === 'guardians' ? 'active' : ''}`}
            onClick={() => setActiveSection('guardians')}
            style={isMobile ? {
              padding: '12px 16px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              flexShrink: 0
            } : {}}
          >
            👨‍👩‍👧 Guardians
          </button>
          <button
            className={`detail-nav-tab ${activeSection === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveSection('schedule')}
            style={isMobile ? {
              padding: '12px 16px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              flexShrink: 0
            } : {}}
          >
            📅 Schedule
          </button>
          <button
            className={`detail-nav-tab ${activeSection === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveSection('attendance')}
            style={isMobile ? {
              padding: '12px 16px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              flexShrink: 0
            } : {}}
          >
            📊 Attendance
          </button>
          <button
            className={`detail-nav-tab ${activeSection === 'competitions' ? 'active' : ''}`}
            onClick={() => setActiveSection('competitions')}
            style={isMobile ? {
              padding: '12px 16px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
              flexShrink: 0
            } : {}}
          >
            🏆 Competitions
          </button>
        </div>

        {/* Content */}
        <div 
          className="student-detail-content"
          style={isMobile ? {
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '15px',
            paddingBottom: '20px'
          } : {}}
        >
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="profile-section">
              <div className="section-header">
                <h3>Student Information</h3>
                {!isEditing ? (
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    setIsEditing(true);
                    setEditedStudent(student);
                  }}>
                    ✏️ Edit Profile
                  </button>
                ) : (
                  <div className="edit-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => {
                      setIsEditing(false);
                      setEditedStudent(student);
                    }}>
                      Cancel
                    </button>
                    <button className="btn btn-success btn-sm" onClick={handleSaveProfile} disabled={loading}>
                      {loading ? 'Saving...' : '💾 Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {!isEditing ? (
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Student ID:</span>
                    <span className="info-value">{student.id}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">First Name:</span>
                    <span className="info-value">{student.firstName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Name:</span>
                    <span className="info-value">{student.lastName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Date of Birth:</span>
                    <span className="info-value">{formatDate(student.dateOfBirth)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Grade Level:</span>
                    <span className="info-value">{student.gradeLevel || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tier:</span>
                    <span className="info-value">
                      {student.tier === 'foundations' && '🌱 Foundations'}
                      {student.tier === 'innovators' && '🚀 Innovators'}
                      {student.tier === 'pioneers' && '🌟 Pioneers'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className="info-value">{student.status || 'Active'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Enrollment Date:</span>
                    <span className="info-value">{student.enrollmentDate ? formatDate(student.enrollmentDate) : 'N/A'}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{student.email || 'N/A'}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">{student.phone || 'N/A'}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Address:</span>
                    <span className="info-value">{student.address || 'N/A'}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Parent Email:</span>
                    <span className="info-value">{student.parentEmail || 'N/A'}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Profile Notes:</span>
                    <span className="info-value">{student.profileNotes || 'No notes'}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Learning Goals:</span>
                    <span className="info-value">{student.learningGoals || 'No goals set'}</span>
                  </div>
                </div>
              ) : (
                <div className="edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={editedStudent.firstName}
                        onChange={(e) => setEditedStudent({...editedStudent, firstName: e.target.value})}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        value={editedStudent.lastName}
                        onChange={(e) => setEditedStudent({...editedStudent, lastName: e.target.value})}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date of Birth *</label>
                      <input
                        type="date"
                        value={editedStudent.dateOfBirth}
                        onChange={(e) => setEditedStudent({...editedStudent, dateOfBirth: e.target.value})}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Grade Level</label>
                      <select
                        value={editedStudent.gradeLevel || ''}
                        onChange={(e) => setEditedStudent({...editedStudent, gradeLevel: e.target.value})}
                        className="form-control"
                      >
                        <option value="">Select Grade</option>
                        <option value="K">Kindergarten</option>
                        <option value="1">Grade 1</option>
                        <option value="2">Grade 2</option>
                        <option value="3">Grade 3</option>
                        <option value="4">Grade 4</option>
                        <option value="5">Grade 5</option>
                        <option value="6">Grade 6</option>
                        <option value="7">Grade 7</option>
                        <option value="8">Grade 8</option>
                        <option value="9">Grade 9</option>
                        <option value="10">Grade 10</option>
                        <option value="11">Grade 11</option>
                        <option value="12">Grade 12</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tier *</label>
                      <select
                        value={editedStudent.tier}
                        onChange={(e) => setEditedStudent({...editedStudent, tier: e.target.value})}
                        className="form-control"
                      >
                        <option value="foundations">🌱 Foundations</option>
                        <option value="innovators">🚀 Innovators</option>
                        <option value="pioneers">🌟 Pioneers</option>
                      </select>
                      <small className="form-hint">Override student tier level</small>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={editedStudent.status || 'active'}
                        onChange={(e) => setEditedStudent({...editedStudent, status: e.target.value})}
                        className="form-control"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="trial">Trial</option>
                        <option value="graduated">Graduated</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={editedStudent.email || ''}
                        onChange={(e) => setEditedStudent({...editedStudent, email: e.target.value})}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editedStudent.phone || ''}
                      onChange={(e) => setEditedStudent({...editedStudent, phone: e.target.value})}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={editedStudent.address || ''}
                      onChange={(e) => setEditedStudent({...editedStudent, address: e.target.value})}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Profile Notes</label>
                    <textarea
                      value={editedStudent.profileNotes || ''}
                      onChange={(e) => setEditedStudent({...editedStudent, profileNotes: e.target.value})}
                      className="form-control"
                      rows={3}
                      placeholder="Medical conditions, allergies, special needs, etc."
                    />
                  </div>

                  <div className="form-group">
                    <label>Learning Goals</label>
                    <textarea
                      value={editedStudent.learningGoals || ''}
                      onChange={(e) => setEditedStudent({...editedStudent, learningGoals: e.target.value})}
                      className="form-control"
                      rows={3}
                      placeholder="Student's learning objectives and goals"
                    />
                  </div>
                </div>
              )}

              {/* Programs Section */}
              <div className="programs-section" style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #e0e0e0' }}>
                <div className="section-header">
                  <h3>📚 Program Enrollments</h3>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowEnrollProgram(!showEnrollProgram)}
                    disabled={isEditing}
                  >
                    {showEnrollProgram ? '✖ Cancel' : '➕ Enroll in Program'}
                  </button>
                </div>

                {showEnrollProgram && (
                  <div className="enroll-program-form" style={{ 
                    background: '#f8f9fa', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #dee2e6'
                  }}>
                    <div className="form-group">
                      <label>Select Program <span className="required">*</span></label>
                      {availablePrograms.length === 0 ? (
                        <div style={{ 
                          padding: '15px', 
                          background: '#fff3cd', 
                          border: '1px solid #ffc107',
                          borderRadius: '6px',
                          color: '#856404',
                          marginBottom: '10px'
                        }}>
                          ⚠️ No active programs available for <strong>{student.tier}</strong> tier. Create programs in the Programs tab first.
                        </div>
                      ) : availablePrograms.filter(p => 
                        !enrolledPrograms.some(ep => ep.program_id === p.id)
                      ).length === 0 ? (
                        <div style={{ 
                          padding: '15px', 
                          background: '#d1ecf1', 
                          border: '1px solid #0c5460',
                          borderRadius: '6px',
                          color: '#0c5460',
                          marginBottom: '10px'
                        }}>
                          ℹ️ Student is already enrolled in all available programs.
                        </div>
                      ) : (
                        <select
                          value={selectedProgramForEnrollment}
                          onChange={(e) => setSelectedProgramForEnrollment(e.target.value)}
                          className="form-control"
                        >
                          <option value="">-- Choose Program --</option>
                          {availablePrograms.filter(p => 
                            !enrolledPrograms.some(ep => ep.program_id === p.id)
                          ).map((program: any) => (
                            <option key={program.id} value={program.id}>
                              {program.name} ({program.tier})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Start Date <span className="required">*</span></label>
                      <input
                        type="date"
                        value={enrollmentStartDate}
                        onChange={(e) => setEnrollmentStartDate(e.target.value)}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date (Optional)</label>
                      <input
                        type="date"
                        value={enrollmentEndDate}
                        onChange={(e) => setEnrollmentEndDate(e.target.value)}
                        className="form-control"
                      />
                      <small className="form-text text-muted">Leave blank if enrollment has no end date</small>
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowEnrollProgram(false);
                          setSelectedProgramForEnrollment('');
                          setEnrollmentStartDate(new Date().toISOString().split('T')[0]);
                          setEnrollmentEndDate('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleEnrollInProgram}
                        disabled={!selectedProgramForEnrollment || !enrollmentStartDate || loading}
                      >
                        {loading ? 'Enrolling...' : '✓ Enroll Student'}
                      </button>
                    </div>
                  </div>
                )}

                {showEditEnrollment && selectedEnrollmentForEdit && (
                  <div className="edit-enrollment-form" style={{ 
                    background: '#f8f9fa', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px' }}>
                      ✏️ Edit Enrollment: {selectedEnrollmentForEdit.program_name || 'Program'}
                    </h4>
                    <div className="form-group">
                      <label>Start Date <span className="required">*</span></label>
                      <input
                        type="date"
                        value={editEnrollmentStartDate}
                        onChange={(e) => setEditEnrollmentStartDate(e.target.value)}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date (Optional)</label>
                      <input
                        type="date"
                        value={editEnrollmentEndDate}
                        onChange={(e) => setEditEnrollmentEndDate(e.target.value)}
                        className="form-control"
                      />
                      <small className="form-text text-muted">Leave blank if enrollment has no end date</small>
                    </div>
                    <div className="form-group">
                      <label>Status <span className="required">*</span></label>
                      <select
                        value={editEnrollmentStatus}
                        onChange={(e) => setEditEnrollmentStatus(e.target.value)}
                        className="form-control"
                      >
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="dropped">Dropped</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Notes</label>
                      <textarea
                        value={editEnrollmentNotes}
                        onChange={(e) => setEditEnrollmentNotes(e.target.value)}
                        className="form-control"
                        rows={3}
                        placeholder="Additional notes about this enrollment..."
                      />
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowEditEnrollment(false);
                          setSelectedEnrollmentForEdit(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleUpdateEnrollment}
                        disabled={!editEnrollmentStartDate || loading}
                      >
                        {loading ? 'Updating...' : '✓ Update Enrollment'}
                      </button>
                    </div>
                  </div>
                )}

                {enrolledPrograms.length === 0 ? (
                  <div className="no-data" style={{ 
                    padding: '30px', 
                    textAlign: 'center', 
                    background: '#fff3cd', 
                    borderRadius: '8px',
                    color: '#856404'
                  }}>
                    <p style={{ margin: 0, fontSize: '15px' }}>
                      ⚠️ No program enrollments found. Click "Enroll in Program" to add one.
                    </p>
                  </div>
                ) : (
                  <div className="programs-list">
                    {enrolledPrograms.map((enrollment: any) => (
                      <div key={enrollment.enrollment_id || enrollment.id} className="program-card" style={{
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '15px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '16px' }}>
                              {enrollment.program_name || 'Unknown Program'}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Status:</span>{' '}
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background: enrollment.status === 'active' ? '#d4edda' : '#f8d7da',
                                  color: enrollment.status === 'active' ? '#155724' : '#721c24',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}>
                                  {enrollment.status || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Tier:</span>{' '}
                                {enrollment.program_tier === 'foundations' && '🌱 Foundations'}
                                {enrollment.program_tier === 'innovators' && '🚀 Innovators'}
                                {enrollment.program_tier === 'pioneers' && '🌟 Pioneers'}
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Start Date:</span>{' '}
                                {enrollment.start_date ? new Date(enrollment.start_date).toLocaleDateString() : 
                                 enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toLocaleDateString() : 'N/A'}
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>End Date:</span>{' '}
                                {enrollment.end_date ? new Date(enrollment.end_date).toLocaleDateString() : 'Not set'}
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Sessions:</span>{' '}
                                {enrollment.sessions_attended || 0} attended
                              </div>
                            </div>
                          </div>
                          <div style={{ marginLeft: '15px', display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleEditEnrollmentClick(enrollment)}
                              disabled={loading || showEditEnrollment}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: loading || showEditEnrollment ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                opacity: loading || showEditEnrollment ? 0.6 : 1,
                                transition: 'background-color 0.2s'
                              }}
                              onMouseOver={(e) => {
                                if (!loading && !showEditEnrollment) {
                                  e.currentTarget.style.backgroundColor = '#0056b3';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!loading && !showEditEnrollment) {
                                  e.currentTarget.style.backgroundColor = '#007bff';
                                }
                              }}
                            >
                              ✏️ Edit
                            </button>
                            {enrollment.status === 'active' && (
                              <button
                                onClick={() => handleUnenrollFromProgram(enrollment.enrollment_id || enrollment.id, enrollment.program_name || 'this program')}
                                disabled={loading || showEditEnrollment}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: loading || showEditEnrollment ? 'not-allowed' : 'pointer',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  opacity: loading || showEditEnrollment ? 0.6 : 1,
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => {
                                  if (!loading && !showEditEnrollment) {
                                    e.currentTarget.style.backgroundColor = '#c82333';
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (!loading && !showEditEnrollment) {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                  }
                                }}
                              >
                                🚫 Unenroll
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Session Packs Section */}
              <div className="session-packs-section" style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #e0e0e0' }}>
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>📦 Session Packs</h3>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      resetSessionPackForm();
                      setShowAddSessionPack(!showAddSessionPack);
                      setShowEditSessionPack(false);
                    }}
                    disabled={isEditing}
                  >
                    {showAddSessionPack ? '✖ Cancel' : '➕ Add Session Pack'}
                  </button>
                </div>

                {showAddSessionPack && (
                  <div className="add-session-pack-form" style={{ 
                    background: '#f8f9fa', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #dee2e6'
                  }}>
                    <h4 style={{ marginTop: 0, marginBottom: '20px' }}>Add New Session Pack</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group">
                        <label>Package Type (Optional)</label>
                        <select
                          value={sessionPackFormData.package_type_id}
                          onChange={(e) => {
                            setSessionPackFormData({ ...sessionPackFormData, package_type_id: e.target.value });
                            // Auto-fill from package type if selected
                            const pkgType = availablePackageTypes.find(p => p.id === e.target.value);
                            if (pkgType) {
                              setSessionPackFormData(prev => ({
                                ...prev,
                                package_type_id: e.target.value,
                                total_sessions: pkgType.session_count.toString(),
                                original_price: pkgType.price.toString(),
                                final_price: pkgType.price.toString(),
                                amount_paid: pkgType.price.toString()
                              }));
                            }
                          }}
                          className="form-control"
                        >
                          <option value="">-- Select Package Type (Optional) --</option>
                          {availablePackageTypes.map((pkg: any) => (
                            <option key={pkg.id} value={pkg.id}>
                              {pkg.name} - {pkg.session_count} sessions - ${pkg.price}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Program (Optional)</label>
                        <select
                          value={sessionPackFormData.program_id}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, program_id: e.target.value })}
                          className="form-control"
                        >
                          <option value="">-- Select Program (Optional) --</option>
                          {enrolledPrograms.map((program: any) => (
                            <option key={program.program_id} value={program.program_id}>
                              {program.program_name || `Program ${program.program_id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Applicable Program Types (Admin Editable)</label>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '6px' }}>
                          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={sessionPackFormData.applicable_program_types.includes('after_school')}
                              onChange={() => toggleApplicableProgramType('after_school')}
                            />
                            After School
                          </label>
                          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={sessionPackFormData.applicable_program_types.includes('home_school')}
                              onChange={() => toggleApplicableProgramType('home_school')}
                            />
                            Home School
                          </label>
                        </div>
                        <small style={{ fontSize: '11px', color: '#666' }}>
                          Select one or both program types where this session pool can be used.
                        </small>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                          Tip: Selecting both creates a shared pool usable in both After School and Home School.
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Total Sessions <span className="required">*</span></label>
                        <input
                          type="number"
                          value={sessionPackFormData.total_sessions}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, total_sessions: e.target.value })}
                          className="form-control"
                          min="1"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Used Sessions</label>
                        <input
                          type="number"
                          value={sessionPackFormData.used_sessions}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, used_sessions: e.target.value })}
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Purchase Date <span className="required">*</span></label>
                        <input
                          type="date"
                          value={sessionPackFormData.purchase_date}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, purchase_date: e.target.value })}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Expiry Date</label>
                        <input
                          type="date"
                          value={sessionPackFormData.expiry_date}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, expiry_date: e.target.value })}
                          className="form-control"
                        />
                        <small style={{ fontSize: '11px', color: '#666' }}>Leave empty to auto-calculate (9 months from purchase)</small>
                      </div>
                      <div className="form-group">
                        <label>Original Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.original_price}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, original_price: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Discount Applied</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.discount_applied}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, discount_applied: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Final Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.final_price}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, final_price: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Amount Paid</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.amount_paid}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, amount_paid: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Payment Status</label>
                        <select
                          value={sessionPackFormData.payment_status}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, payment_status: e.target.value })}
                          className="form-control"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="partial">Partial</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={sessionPackFormData.status}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, status: e.target.value })}
                          className="form-control"
                        >
                          <option value="active">Active</option>
                          <option value="expired">Expired</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Notes</label>
                        <textarea
                          value={sessionPackFormData.notes}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, notes: e.target.value })}
                          className="form-control"
                          rows={3}
                          placeholder="Optional notes about this session pack"
                        />
                      </div>
                    </div>
                    <div className="form-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowAddSessionPack(false);
                          resetSessionPackForm();
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleAddSessionPack}
                        disabled={!sessionPackFormData.total_sessions || !sessionPackFormData.purchase_date || loading}
                      >
                        {loading ? 'Adding...' : '✓ Add Session Pack'}
                      </button>
                    </div>
                  </div>
                )}

                {showEditSessionPack && selectedSessionPack && (
                  <div className="edit-session-pack-form" style={{ 
                    background: '#fff3cd', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #ffc107'
                  }}>
                    <h4 style={{ marginTop: 0, marginBottom: '20px' }}>Edit Session Pack</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Applicable Program Types (Admin Editable)</label>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '6px' }}>
                          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={sessionPackFormData.applicable_program_types.includes('after_school')}
                              onChange={() => toggleApplicableProgramType('after_school')}
                            />
                            After School
                          </label>
                          <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={sessionPackFormData.applicable_program_types.includes('home_school')}
                              onChange={() => toggleApplicableProgramType('home_school')}
                            />
                            Home School
                          </label>
                        </div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                          Tip: Selecting both creates a shared pool usable in both After School and Home School.
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Total Sessions <span className="required">*</span></label>
                        <input
                          type="number"
                          value={sessionPackFormData.total_sessions}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, total_sessions: e.target.value })}
                          className="form-control"
                          min="1"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Used Sessions</label>
                        <input
                          type="number"
                          value={sessionPackFormData.used_sessions}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, used_sessions: e.target.value })}
                          className="form-control"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Purchase Date <span className="required">*</span></label>
                        <input
                          type="date"
                          value={sessionPackFormData.purchase_date}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, purchase_date: e.target.value })}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Expiry Date</label>
                        <input
                          type="date"
                          value={sessionPackFormData.expiry_date}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, expiry_date: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Original Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.original_price}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, original_price: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Final Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.final_price}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, final_price: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Amount Paid</label>
                        <input
                          type="number"
                          step="0.01"
                          value={sessionPackFormData.amount_paid}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, amount_paid: e.target.value })}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Payment Status</label>
                        <select
                          value={sessionPackFormData.payment_status}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, payment_status: e.target.value })}
                          className="form-control"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="partial">Partial</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={sessionPackFormData.status}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, status: e.target.value })}
                          className="form-control"
                        >
                          <option value="active">Active</option>
                          <option value="expired">Expired</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Notes</label>
                        <textarea
                          value={sessionPackFormData.notes}
                          onChange={(e) => setSessionPackFormData({ ...sessionPackFormData, notes: e.target.value })}
                          className="form-control"
                          rows={3}
                        />
                      </div>
                    </div>
                    <div className="form-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowEditSessionPack(false);
                          setSelectedSessionPack(null);
                          resetSessionPackForm();
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleEditSessionPack}
                        disabled={loading}
                      >
                        {loading ? 'Updating...' : '✓ Update Session Pack'}
                      </button>
                    </div>
                  </div>
                )}

                {sessionPacks.length === 0 ? (
                  <div className="no-data" style={{ 
                    padding: '30px', 
                    textAlign: 'center', 
                    background: '#fff3cd', 
                    borderRadius: '8px',
                    color: '#856404'
                  }}>
                    <p style={{ margin: 0, fontSize: '15px' }}>
                      ⚠️ No session packs found. Click "Add Session Pack" to add one.
                    </p>
                  </div>
                ) : (
                  <div className="session-packs-list">
                    {sessionPacks.map((pack: any) => (
                      <div key={pack.id} className="session-pack-card" style={{
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '15px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                              <h4 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
                                {pack.package_type?.name || `Session Pack (${pack.total_sessions} sessions)`}
                              </h4>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                background: pack.status === 'active' ? '#d4edda' : pack.status === 'expired' ? '#f8d7da' : '#fff3cd',
                                color: pack.status === 'active' ? '#155724' : pack.status === 'expired' ? '#721c24' : '#856404',
                                fontSize: '12px',
                                fontWeight: 600
                              }}>
                                {pack.status || 'N/A'}
                              </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '13px' }}>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Total Sessions:</span>{' '}
                                <strong>{pack.total_sessions}</strong>
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Used:</span>{' '}
                                <strong>{pack.used_sessions || 0}</strong>
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Remaining:</span>{' '}
                                <strong style={{ color: pack.remaining_sessions > 0 ? '#28a745' : '#dc3545' }}>
                                  {pack.remaining_sessions || 0}
                                </strong>
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Purchase Date:</span>{' '}
                                {pack.purchase_date ? new Date(pack.purchase_date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                }) : 'N/A'}
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Expiry Date:</span>{' '}
                                {pack.expiry_date ? new Date(pack.expiry_date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                }) : 'N/A'}
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Final Price:</span>{' '}
                                <strong>${parseFloat(pack.final_price || 0).toFixed(2)}</strong>
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Amount Paid:</span>{' '}
                                <strong>${parseFloat(pack.amount_paid || 0).toFixed(2)}</strong>
                              </div>
                              <div>
                                <span style={{ color: '#666', fontWeight: 600 }}>Payment Status:</span>{' '}
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  background: pack.payment_status === 'paid' ? '#d4edda' : '#fff3cd',
                                  color: pack.payment_status === 'paid' ? '#155724' : '#856404',
                                  fontSize: '11px'
                                }}>
                                  {pack.payment_status || 'pending'}
                                </span>
                              </div>
                              {pack.program_id && (
                                <div>
                                  <span style={{ color: '#666', fontWeight: 600 }}>Program:</span>{' '}
                                  {enrolledPrograms.find((p: any) => p.program_id === pack.program_id)?.program_name || 'N/A'}
                                </div>
                              )}
                              {Array.isArray(pack.applicable_program_types) && pack.applicable_program_types.length > 0 && (
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <span style={{ color: '#666', fontWeight: 600 }}>Applicable Types:</span>{' '}
                                  {pack.applicable_program_types.map((type: string) => type.replace('_', ' ')).join(', ')}
                                </div>
                              )}
                            </div>
                            {pack.notes && (
                              <div style={{ marginTop: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', fontSize: '12px' }}>
                                <strong>Notes:</strong> {pack.notes}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                            <button
                              className="btn btn-sm"
                              onClick={() => handleEditSessionPackClick(pack)}
                              style={{ 
                                padding: '6px 12px', 
                                fontSize: '12px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="btn btn-sm"
                              onClick={() => handleDeleteSessionPack(pack.id)}
                              style={{ 
                                padding: '6px 12px', 
                                fontSize: '12px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'guardians' && (
            <div className="profile-section">
              <div className="section-header">
                <h3>Guardian Information</h3>
              </div>
              {guardiansLoading ? (
                <div style={{ padding: '20px', color: '#666' }}>Loading guardians...</div>
              ) : guardians.length === 0 ? (
                <div style={{ padding: '20px', color: '#666' }}>No guardians found for this student.</div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {guardians.map((guardian) => (
                    <div
                      key={`${guardian.kind}-${guardian.id}`}
                      style={{
                        border: guardian.is_primary ? '2px solid #2b6cb0' : '1px solid #d9dee7',
                        backgroundColor: guardian.is_primary ? '#f0f7ff' : '#fff',
                        borderRadius: '10px',
                        padding: '14px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '16px', color: '#1f2937' }}>
                          {guardian.first_name} {guardian.last_name}
                        </strong>
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '4px 10px',
                            borderRadius: '999px',
                            backgroundColor: guardian.is_primary ? '#dbeafe' : '#eef2f7',
                            color: guardian.is_primary ? '#1e40af' : '#334155'
                          }}
                        >
                          {guardian.is_primary ? 'Primary Guardian' : 'Guardian'}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '8px 14px', fontSize: '14px', color: '#4b5563' }}>
                        <div><strong>Email:</strong> {guardian.email || 'N/A'}</div>
                        <div><strong>Phone:</strong> {guardian.phone || 'N/A'}</div>
                        <div><strong>Alternate Phone:</strong> {guardian.alternate_phone || 'N/A'}</div>
                        <div><strong>User Type:</strong> {guardian.user_type || 'N/A'}</div>
                        <div><strong>Relationship:</strong> {guardian.relationship_to_student || 'N/A'}</div>
                        <div><strong>Source:</strong> {guardian.kind === 'linked_contact' ? 'Linked Contact' : 'Guardian User'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Schedule Section */}
          {activeSection === 'schedule' && (
            <div className="schedule-section">
              <div className="section-header">
                <h3>Session Schedule</h3>
              </div>

              <div className="schedule-form" style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label>Program <span className="required">*</span></label>
                  {enrolledPrograms.length === 0 ? (
                    <p className="no-data">No enrolled programs found. Enroll student in a program first.</p>
                  ) : (
                    <select
                      value={selectedProgramId}
                      onChange={(e) => setSelectedProgramId(e.target.value)}
                      className="form-control"
                    >
                      <option value="">-- Select Program --</option>
                      {enrolledPrograms.map((enrollment: any) => (
                        <option key={enrollment.program_id} value={enrollment.program_id}>
                          {enrollment.program_name || `Program ${enrollment.program_id}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="time-selection">
                  <div className="form-group">
                    <label>Start Time (PST)</label>
                    <input
                      type="time"
                      value={sessionStartTime}
                      onChange={(e) => setSessionStartTime(e.target.value)}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time (PST)</label>
                    <input
                      type="time"
                      value={sessionEndTime}
                      onChange={(e) => setSessionEndTime(e.target.value)}
                      className="form-control"
                    />
                  </div>
                </div>

                <p className="instruction" style={{ marginBottom: 0 }}>
                  💡 <strong>Click on a calendar day to schedule/unschedule</strong>
                  <br />
                  <small>🟡 Orange = Has session (click to remove) | ⬜ White = Empty (click to schedule)</small>
                </p>
              </div>

              {renderScheduleCalendar()}
            </div>
          )}

          {/* Attendance Section */}
          {activeSection === 'attendance' && (
            <div className="attendance-section">
              <div className="section-header">
                <h3>Attendance Tracking</h3>
              </div>

              {renderAttendanceCalendar()}
            </div>
          )}

          {/* Competitions Section */}
          {activeSection === 'competitions' && (
            <div className="competitions-section">
              <div className="section-header">
                <h3>Competition Streams (Add-ons)</h3>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowAddCompetition(!showAddCompetition)}
                >
                  {showAddCompetition ? '✖ Cancel' : '➕ Add Competition'}
                </button>
              </div>

              {/* Add Competition Form */}
              {showAddCompetition && (
                <div className="add-competition-form">
                  <h4 style={{ marginBottom: '20px', color: '#333' }}>Enroll in Competition</h4>
                  
                  {availableCompetitions.length === 0 ? (
                    <div className="no-competitions-notice">
                      <p>⚠️ No active competitions available for {student.tier} tier</p>
                      <p style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
                        Create a competition in the <strong>Competitions</strong> tab first.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="form-group">
                        <label>Select Competition <span className="required">*</span></label>
                        <select
                          value={selectedCompetition}
                          onChange={(e) => setSelectedCompetition(e.target.value)}
                          className="form-control"
                        >
                          <option value="">-- Choose Competition --</option>
                          {availableCompetitions.map(comp => (
                            <option key={comp.id} value={comp.id}>
                              {comp.name} ({comp.package_name} - ${Number(comp.package_price || 0).toFixed(2)})
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedCompetition && (
                        <>
                          <div className="form-group">
                            <label>Team Name (Optional)</label>
                            <input
                              type="text"
                              value={teamName}
                              onChange={(e) => setTeamName(e.target.value)}
                              placeholder="e.g., OARA Robotics Team A"
                              className="form-control"
                            />
                          </div>

                          <div className="form-group">
                            <label>Team Size <span className="required">*</span></label>
                            <input
                              type="number"
                              value={teamSize}
                              onChange={(e) => setTeamSize(parseInt(e.target.value) || 5)}
                              min="1"
                              max="20"
                              className="form-control"
                            />
                            <small className="form-hint">Used to calculate shared costs per student</small>
                          </div>

                          {/* Cost Breakdown */}
                          {(() => {
                            const comp = availableCompetitions.find(c => c.id === selectedCompetition);
                            if (!comp) return null;

                            const sessionPackCost = comp.package_price ? Number(comp.package_price) : 0;
                            const tshirtCost = comp.tshirt_cost ? Number(comp.tshirt_cost) : 0;
                            const teamRegShare = comp.team_registration_fee ? Number(comp.team_registration_fee) / teamSize : 0;
                            const tournamentRegShare = comp.tournament_registration_fee ? Number(comp.tournament_registration_fee) / teamSize : 0;
                            const otherCostsShare = comp.other_costs ? Number(comp.other_costs) / teamSize : 0;
                            const total = sessionPackCost + tshirtCost + teamRegShare + tournamentRegShare + otherCostsShare;

                            return (
                              <div className="cost-breakdown">
                                <h4>💰 Cost Breakdown</h4>
                                
                                <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #eee' }}>
                                  <strong>Individual Fees:</strong>
                                </div>
                                <div className="cost-item">
                                  <span>Session Pack ({comp.package_sessions} sessions):</span>
                                  <span>${sessionPackCost.toFixed(2)}</span>
                                </div>
                                {tshirtCost > 0 && (
                                  <div className="cost-item">
                                    <span>T-Shirt:</span>
                                    <span>${tshirtCost.toFixed(2)}</span>
                                  </div>
                                )}
                                
                                <div style={{ marginTop: '12px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #eee' }}>
                                  <strong>Shared Fees (Team of {teamSize}):</strong>
                                </div>
                                {teamRegShare > 0 && (
                                  <div className="cost-item">
                                    <span>Team Registration (${Number(comp.team_registration_fee).toFixed(2)} ÷ {teamSize}):</span>
                                    <span>${teamRegShare.toFixed(2)}</span>
                                  </div>
                                )}
                                {tournamentRegShare > 0 && (
                                  <div className="cost-item">
                                    <span>Tournament Registration (${Number(comp.tournament_registration_fee).toFixed(2)} ÷ {teamSize}):</span>
                                    <span>${tournamentRegShare.toFixed(2)}</span>
                                  </div>
                                )}
                                {otherCostsShare > 0 && (
                                  <div className="cost-item">
                                    <span>Other Costs (${Number(comp.other_costs).toFixed(2)} ÷ {teamSize}):</span>
                                    <span>${otherCostsShare.toFixed(2)}</span>
                                  </div>
                                )}
                                
                                <div className="cost-item total" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #333' }}>
                                  <span><strong>Total Cost:</strong></span>
                                  <span><strong>${total.toFixed(2)}</strong></span>
                                </div>
                              </div>
                            );
                          })()}

                          <div className="form-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowAddCompetition(false);
                                setSelectedCompetition('');
                                setTeamName('');
                                setTeamSize(5);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleAddCompetition}
                              disabled={loading || !selectedCompetition}
                            >
                              {loading ? 'Enrolling...' : 'Enroll in Competition'}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Current Competition Enrollments */}
              <div className="current-competitions">
                <h4>Current Competition Streams</h4>
                
                {competitionEnrollments.length === 0 ? (
                  <div className="empty-competitions">
                    <p>🎯 No competition streams added yet</p>
                    <p style={{ fontSize: '13px', color: '#666' }}>
                      Add FLL Explore or FLL Challenge to this student's enrollment
                    </p>
                  </div>
                ) : (
                  <div className="competitions-list">
                    {competitionEnrollments.map((enrollment) => (
                      <div key={enrollment.id} className="competition-enrollment-card">
                        <div className="enrollment-header">
                          <div>
                            <h4 className="competition-name">
                              🤖 {enrollment.competition_name || 'Competition'}
                            </h4>
                            {enrollment.team_name && (
                              <p className="team-name">Team: {enrollment.team_name}</p>
                            )}
                          </div>
                          <span className={`status-badge status-${enrollment.status}`}>
                            {enrollment.status}
                          </span>
                        </div>

                        <div className="enrollment-details">
                          <div className="detail-row">
                            <span className="detail-label">Enrolled:</span>
                            <span className="detail-value">{formatDate(enrollment.enrollment_date)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Total Cost:</span>
                            <span className="detail-value">${Number(enrollment.total_cost).toFixed(2)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Amount Paid:</span>
                            <span className="detail-value">${Number(enrollment.amount_paid).toFixed(2)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Balance:</span>
                            <span className="detail-value balance">
                              ${(Number(enrollment.total_cost) - Number(enrollment.amount_paid)).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <div className="enrollment-actions">
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveCompetition(enrollment.id)}
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="student-detail-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailView;
